import React, { useState } from 'react';
import { Field, Label, Switch } from '@headlessui/react';
import clsx from 'clsx';

const Summary = ({ data, edit, finalSubmit }) => {
  const [enabled, setEnabled] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const renderStepSummary = (step, headline, fields) => {
    return (
      <div className="step-summary">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">{headline}</h3>
          <button onClick={() => edit(step, data[`step${step}`])} className="text-white mt-1 hover:text-purple transition duration-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9.75h4.875a2.625 2.625 0 0 1 0 5.25H12M8.25 9.75 10.5 7.5M8.25 9.75 10.5 12m9-7.243V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z" />
            </svg>
          </button>
        </div>
        {fields.map(field => (
          Array.isArray(field) && field.length === 2 && field[1] && (
            <Field className="flex justify-between items-center py-2" key={field[0]}>
              <Label className="text-white text-sm/5 font-semibold">{field[0]}</Label>
              <p className="text-white/80 text-sm/5">{field[1]}</p>
            </Field>
          )
        ))}
      </div>
    );
  };

  const handleFinalSubmit = () => {
    setTriedSubmit(true);
    if (enabled) {
      setLoading(true);
      setTimeout(() => {
        finalSubmit();
      }, 500);
    }
  };

  // Format referral source for display
  const formatReferralSource = () => {
    if (data.step4.referralSource === 'Sonstiges (bitte angeben)') {
      return `${data.step4.referralSource}: ${data.step4.otherReferralSource}`;
    }
    return data.step4.referralSource;
  };

  return (
    <div className="summary text-white flex flex-col gap-12">
      <h2 className="text-2xl font-bold">Überprüfe und bearbeite deine Informationen</h2>
      {renderStepSummary(1, 'Nominierungs-Kriterien', [
        ['Ist die nominierte Person zwischen 16 und 24 Jahre alt?', data.step1.age ? 'Ja' : 'Nein'],
        ['Engagiert sich die nominierte Person in Deutschland?', data.step1.inGermany ? 'Ja' : 'Nein'],
        ['Hast du mit der nominierten Person gesprochen und sein/ihr Einverständnis?', data.step1.nomineeKnows ? 'Ja' : 'Nein'],
        ['In welchem Bereich engagiert sich die nominierte Person?', data.step1.selectedOption],
      ])}
      {renderStepSummary(2, 'Kontaktdaten der nominierten Person', [
        ['Name', data.step2.name],
        ['Alter', data.step2.age],
        ['E-Mail', data.step2.email],
        ['Kontakt-Telefon', data.step2.telephone || 'Nicht angegeben'],
        ['Stadt/Region', data.step2.place],
        ['Social Media / Website Link', data.step2.socialMediaLink || 'Nicht angegeben'],
        ['Zusätzliche Links', data.step2.socialMediaLinks && data.step2.socialMediaLinks.length > 0 ? data.step2.socialMediaLinks.join(', ') : 'Keine'],
        ['Engagement Typ', data.step2.engagementAloneOrTeam === 'alone' ? 'Allein' : 'Teil eines Teams'],
        data.step2.engagementAloneOrTeam === 'team' && ['Team Mitglied 1 Name', data.step2.teamMember1Name || 'Nicht angegeben'],
        data.step2.engagementAloneOrTeam === 'team' && ['Team Mitglied 1 Alter', data.step2.teamMember1Age || 'Nicht angegeben'],
        data.step2.engagementAloneOrTeam === 'team' && data.step2.teamMember1Email && ['Team Mitglied 1 E-Mail', data.step2.teamMember1Email],
        data.step2.engagementAloneOrTeam === 'team' && data.step2.teamMember1Telephone && ['Team Mitglied 1 Telefon', data.step2.teamMember1Telephone],
        data.step2.engagementAloneOrTeam === 'team' && ['Team Mitglied 2 Name', data.step2.teamMember2Name || 'Nicht angegeben'],
        data.step2.engagementAloneOrTeam === 'team' && ['Team Mitglied 2 Alter', data.step2.teamMember2Age || 'Nicht angegeben'],
        data.step2.engagementAloneOrTeam === 'team' && data.step2.teamMember2Email && ['Team Mitglied 2 E-Mail', data.step2.teamMember2Email],
        data.step2.engagementAloneOrTeam === 'team' && data.step2.teamMember2Telephone && ['Team Mitglied 2 Telefon', data.step2.teamMember2Telephone],
      ])}
      {renderStepSummary(3, 'Engagement-Details', [
        ['Kurze Beschreibung des Engagements', data.step3.engagementDescription],
        ['Warum denkst du, sollte die nominierte Person Teil der Movers of Tomorrow werden?', data.step3.engagementWhy],
        data.step3.howDidYouHear && ['Wie hast du von dem Engagement der nominierten Person erfahren?', data.step3.howDidYouHear],
        data.step3.additionalNotes && ['Gibt es sonst noch etwas, das du zum Engagement oder zu der nominierten Person hinzufügen möchtest?', data.step3.additionalNotes],
        data.step3.files && data.step3.files.length > 0 && ['Unterlagen zur Unterstützung', data.step3.files.map(file => file.name).join(', ')]
      ])}
      {renderStepSummary(4, 'Informationen zu Dir', [
        ['Name', data.step4.Nominator],
        ['Beziehung zu der nominierten Person', data.step4.NominatorRelationship],
        ['E-Mail', data.step4.NominatorEmail],
        ['Telefon', data.step4.NominatorTelephone || 'Nicht angegeben'],
        ['Organisation/Institution', data.step4.NominatorInstitution || 'Nicht angegeben'],
        data.step4.agencyId && ['AgenturId', data.step4.agencyId],
        ['Wie bist Du auf den Movers of Tomorrow Award aufmerksam geworden?', formatReferralSource()],
        ['Einverständnis Datenschutz nominierte Person', data.step4.contactConsent ? 'Ja' : 'Nein'],
        ['Einwilligung zu weiteren Informationen zum Award', data.step4.infoConsent ? 'Ja' : 'Nein'],
        ['Allianz Foundation Newsletter', data.step4.newsletterSubscription ? 'Ja' : 'Nein']
      ])}
      <nav className="progress-nav2 h-32 sm:h-24 mt-8">
        <div className="inner flex flex-col sm:flex-row h-full sm:items-center justify-between py-3">
          <div className="flex items-center">
            <Switch
              checked={enabled}
              onChange={(value) => {
                setEnabled(value);
                if (value) setTriedSubmit(false);
              }}
              className={clsx(
                'relative flex h-7 w-20 sm:w-14 cursor-pointer rounded-full p-1 transition-colors duration-200 ease-in-out',
                enabled ? 'bg-purple' : 'bg-orange',
                triedSubmit && !enabled && 'bg-orange ring-2 ring-inset ring-orange/80'
              )}
            >
              <span
                aria-hidden="true"
                className={clsx(
                  'inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out',
                  enabled ? 'translate-x-7' : 'translate-x-0'
                )}
              />
            </Switch>
            <p className="ml-3 text-white relative -mt-1">Ich bestätige, dass die oben genannten Informationen korrekt sind.</p>
          </div>
          <button onClick={handleFinalSubmit} className={clsx(
            'text-black h-12 px-8 ring-inset ring-2 ring-green transition-all duration-200',
            enabled ? 'bg-green hover:bg-green/90 hover:ring-black/10' : 'ring-black/0 bg-white/50 cursor-not-allowed'
          )}>
            <div className="flex gap-1.5 justify-center sm:items-center">
              <span className="font-semibold tracking-wide -mt-0.5 block text-lg">Nominierung absenden</span>
              {loading && (
                <svg className="animate-spin mt-1 sm:mt-0 -mr-1 ml-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Summary;